<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import Banner from 'dashboard/components/ui/Banner.vue';
import WahaPairDialog from './WahaPairDialog.vue';
import wahaApi from './api';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const pairDialog = ref(null);
let statusTimer = null;

const storeInbox = computed(() =>
  store.getters['inboxes/getInbox'](props.inbox.id)
);
const disconnected = computed(() => {
  const waha = storeInbox.value?.waha || props.inbox?.waha;
  return !!waha && !waha.connected;
});

const patchConnection = waha => {
  if (!props.inbox?.id || !waha) return;

  store.dispatch('inboxes/updateWahaConnection', {
    id: props.inbox.id,
    waha,
    reauthorizationRequired: !waha.connected,
  });
};

const syncStatus = async () => {
  const { data } = await wahaApi.sessionStatus(props.inbox.id);
  patchConnection(data.waha || {});
};

const stopStatusPolling = () => {
  if (statusTimer) clearInterval(statusTimer);
  statusTimer = null;
};

const startStatusPolling = () => {
  stopStatusPolling();
  if (!disconnected.value || !props.inbox?.id) return;

  statusTimer = setInterval(async () => {
    try {
      await syncStatus();
      if (!disconnected.value) stopStatusPolling();
    } catch {
      // retry on next poll
    }
  }, 3000);
};

watch(disconnected, visible => {
  if (visible) startStatusPolling();
  else stopStatusPolling();
});

onMounted(async () => {
  if (!props.inbox?.id) return;

  try {
    await syncStatus();
  } catch {
    // poll or stay with the cached snapshot
  }
  if (disconnected.value) startStatusPolling();
});

onUnmounted(stopStatusPolling);

const openPairDialog = () => {
  pairDialog.value?.open();
};
</script>

<template>
  <Banner
    v-if="disconnected"
    color-scheme="alert"
    class="mt-2 mx-2 rounded-lg overflow-hidden"
    :banner-message="$t('CONVERSATION.INBOX.WAHA.NOT_CONNECTED')"
    has-action-button
    :action-button-label="$t('CONVERSATION.INBOX.WAHA.RECONNECT')"
    @primary-action="openPairDialog"
  />
  <WahaPairDialog ref="pairDialog" :inbox="inbox" />
</template>
